import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import "./App.css";
import HomePageS from "./components/homepage/HomePageS";
import Ourteam from "./components/ourteam/Ourteam";
import About from "./components/about/About";
import Navbar from "./components/navbar/Navbar";
import HivePort from "./components/hive_port/HivePort";
import vid from "./assets/backgrounds/loader.webm";
import linie1 from "./assets/backgrounds/linie1.webp";
import linie2 from "./assets/backgrounds/linie2.webp";
import Service from "./components/services/Services";
import Contact from "./components/contact/Contact";
import Member from "./components/member/Member";
import ProjectShowcase from "./components/project_showcase/ProjectShowcase";
import Footer from "./components/footer/Footer";
import welcomeSound from "./assets/welcomesound/weelcome.mp3";
import Terms from "./components/legal/Terms";
import Privacy from "./components/legal/Privacy";
import BlogPage from "./components/blog/BlogPage";
import BlogArticle from "./components/blog/BlogArticle";
import BrandFounderPage from "./components/brand_founder/BrandFounderPage";
import logocubg from "./assets/logos/logocubg.png";
import hivebg from "./assets/logos/hivebg.png";

function Preloader() {
  const location = useLocation();
  const isJustLupusRoute = location.pathname === "/justlupus";

  useEffect(() => {
    if (isJustLupusRoute) {
      document.body.classList.add("no-animation");
    } else {
      document.body.classList.remove("no-animation");
    }
  }, [isJustLupusRoute]);

  if (isJustLupusRoute) return null;

  return (
    <div className="preLoader">
      <video id="videoloader" playsInline muted autoPlay>
        <source src={vid} type="video/webm" />
      </video>

      <div className="liniile_nemuririi">
        <img src={linie1} className="linie1" alt="linie1" />
        <img src={linie2} className="linie2" alt="linie2" />
      </div>
    </div>
  );
}

function App() {
  const websiteContentRef = useRef(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const audioRef = useRef(null);
  const hasPlayedRef = useRef(false);

  useEffect(() => {
    audioRef.current = new Audio(welcomeSound);
    audioRef.current.volume = 1.0;
    audioRef.current.preload = "auto";

    const tryPlay = () => {
      if (hasPlayedRef.current || !audioRef.current) return;
      audioRef.current.currentTime = 0;
      audioRef.current
        .play()
        .then(() => {
          hasPlayedRef.current = true;
          document.removeEventListener("click", tryPlay);
          document.removeEventListener("touchstart", tryPlay);
          document.removeEventListener("scroll", tryPlay);
        })
        .catch((error) => {
          console.log("Autoplay blocked, will retry on interaction:", error);
        });
    };

    tryPlay();

    document.addEventListener("click", tryPlay, { once: false });
    document.addEventListener("touchstart", tryPlay, { once: false });
    document.addEventListener("scroll", tryPlay, { once: false });

    return () => {
      document.removeEventListener("click", tryPlay);
      document.removeEventListener("touchstart", tryPlay);
      document.removeEventListener("scroll", tryPlay);
    };
  }, []);

  const handleProjectClick = (projectId) => {
    setSelectedProjectId(projectId);
  };

  const handleCloseProjectShowcase = () => {
    setSelectedProjectId(null);
  };

  const defaultTitle =
    "HiveTeam | Creare Site-uri Web Design Interior & Amenajari Romania";
  const defaultDescription =
    "HiveTeam – Agentie web design Romania specializata in creare site-uri pentru firme de design interior, amenajari interioare si arhitectura. Website-uri profesionale, brand identity, SEO si graphic design.";
  const defaultKeywords = [
    "HiveTeam",
    "Pradais Samuel",
    "JustLupus",
    "creare site design interior",
    "website amenajari interioare",
    "site web arhitect Romania",
    "creare site-uri web Romania",
    "web design Romania",
    "agentie web Romania",
    "website firma amenajari",
    "site profesional arhitectura",
    "brand identity design interior",
    "portofoliu online designer interior",
    "web design amenajari interioare",
    "creare site firma constructii",
    "web designer Romania",
    "UI UX design",
    "identitate vizuala",
    "logo design Romania",
    "brand book",
    "SEO Romania",
    "agentie digitala Romania",
    "Hive Team Romania",
  ].join(", ");

  const canonicalUrl =
    typeof window !== "undefined"
      ? window.location.href.split("#")[0]
      : "https://hiveteam.dev";

  const canonicalOrigin =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://hiveteam.dev";

  const shareImage = hivebg.startsWith("http")
    ? hivebg
    : `${canonicalOrigin}${hivebg}`;

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "HiveTeam",
    url: "https://hiveteam.dev",
    logo: logocubg,
    description: defaultDescription,
    sameAs: [
      "https://hiveteam.dev",
      "https://discord.gg/hiveteam",
      "https://www.instagram.com/hive.team/",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      email: "office@hiveteam.dev",
      contactType: "customer support",
      availableLanguage: ["en", "ro"],
    },
    areaServed: "RO",
    founder: {
      "@type": "Person",
      name: "Pradais Samuel",
      alternateName: "JustLupus",
      url: "https://hiveteam.dev/justlupus",
    },
    serviceType: [
      "Creare site-uri web",
      "Brand identity",
      "Graphic design",
      "UI/UX",
    ],
  };

  return (
    <HelmetProvider>
      <Helmet>
        <title>{defaultTitle}</title>
        <meta name="description" content={defaultDescription} />
        <meta name="keywords" content={defaultKeywords} />
        <link rel="icon" href={logocubg} />
        <meta property="og:title" content={defaultTitle} />
        <meta property="og:description" content={defaultDescription} />
        <meta property="og:image" content={shareImage} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="HiveTeam" />
        <meta
          property="og:url"
          content={
            typeof window !== "undefined"
              ? window.location.href
              : "https://hiveteam.dev"
          }
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={defaultTitle} />
        <meta name="twitter:description" content={defaultDescription} />
        <meta name="twitter:image" content={shareImage} />
        <meta
          name="google-site-verification"
          content="_EumPlqRLlNNDnuUeSKYk6QhD2xbaq16Gw7Fi5uJSsg"
        />
        <meta name="geo.region" content="RO" />
        <meta name="geo.placename" content="Romania" />
        <meta name="geo.position" content="45;25" />
        <meta name="ICBM" content="45, 25" />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>
      <Router>
        <div className="App">
          <Preloader />
          <div className="websiteContent" ref={websiteContentRef}>
            <Routes>
              <Route
                path="/justlupus"
                element={<Member memberSlug="lupus" />}
              />
              <Route
                path="/pradais-samuel"
                element={<Navigate to="/justlupus" replace />}
              />
              <Route
                path="/lupus"
                element={<Navigate to="/justlupus" replace />}
              />
              <Route
                path="/merilus"
                element={<Member memberSlug="merilus" />}
              />
              <Route
                path="/korrupt"
                element={<Member memberSlug="korrupt" />}
              />
              <Route
                path="/mariommm"
                element={<Member memberSlug="mariommm" />}
              />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogArticle />} />
              <Route
                path="/hiveteam-pradais-samuel"
                element={<BrandFounderPage />}
              />
              <Route
                path="/brand-founder"
                element={<Navigate to="/hiveteam-pradais-samuel" replace />}
              />
              <Route
                path="/*"
                element={
                  <>
                    {selectedProjectId && (
                      <ProjectShowcase
                        projectId={selectedProjectId}
                        onClose={handleCloseProjectShowcase}
                      />
                    )}
                    <HomePageS />
                    <About />
                    <Ourteam />
                    <HivePort onProjectClick={handleProjectClick} />
                    <Service />
                    <Contact />
                    <Footer contentRef={websiteContentRef} />
                    <Navbar contentRef={websiteContentRef} />
                  </>
                }
              />
            </Routes>
          </div>
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
